/**
 * Bewegung mit Physik, nicht mit Dauer.
 *
 * Ein `ease-in-out` über 200 ms ist Material 2. Expressive bewegt Dinge so,
 * wie Dinge sich bewegen: Sie haben Masse, sie schwingen kurz nach, und sie
 * kommen unterschiedlich schnell zur Ruhe, je nachdem wie weit sie mussten.
 * Genau das leistet eine Feder und eine Kurve mit fester Dauer nicht.
 *
 * Die Parameter stehen hier und nur hier — keine Zahlen in Komponenten.
 */

import type { Transition, Variants } from 'framer-motion'

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Ohne Feder, ohne Dauer — für alle, die keine Bewegung wollen. */
const instant: Transition = { duration: 0 }

function spring(config: Transition): Transition {
  return prefersReducedMotion() ? instant : config
}

/** Knapp und sachlich. Für Zustandswechsel, die nicht auffallen sollen. */
export const springSnappy = (): Transition =>
  spring({ type: 'spring', stiffness: 420, damping: 34, mass: 0.8 })

/** Weich und getragen. Für Flächen, die kommen und gehen. */
export const springSoft = (): Transition =>
  spring({ type: 'spring', stiffness: 260, damping: 30, mass: 1 })

/** Mit Überschwingen. Sparsam einsetzen — sonst wirkt die Oberfläche zappelig. */
export const springBouncy = (): Transition =>
  spring({ type: 'spring', stiffness: 380, damping: 18, mass: 0.9 })

/** Auftritt einer Seite: von unten herein, leicht heranfahrend. */
export const pageVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
}

/**
 * Gestaffelter Auftritt einer Liste.
 *
 * Der Versatz ist bewusst klein: Bei 60 ms je Element wartet man bei zwanzig
 * Zeilen über eine Sekunde, bis die letzte da ist. Deshalb zusätzlich gedeckelt.
 */
export const listVariants = (count: number): Variants => ({
  hidden: {},
  visible: {
    transition: {
      // Auch der Versatz muss unter `prefers-reduced-motion` weg. Die
      // Federn der Kinder sind dann zwar sofort fertig, aber die Zeilen
      // tröpfelten weiter über eine halbe Sekunde herein — und genau dieses
      // Nacheinander ist die Bewegung, die jemandem übel wird.
      staggerChildren: prefersReducedMotion() ? 0 : Math.min(0.04, 0.4 / Math.max(count, 1)),
    },
  },
})

export const listItemVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
}

/**
 * Shape-Morphing beim Drücken: Die Pille wird kurz zum Squircle.
 *
 * Als Style-Objekt statt als Klasse, weil der Radius animiert wird und CSS
 * dafür keinen Zwischenwert zwischen zwei Custom Properties kennt.
 *
 * ⚠️ Eine **Funktion**, wie alles andere hier. Als Konstante wurde
 * `prefersReducedMotion()` genau einmal beim Laden des Moduls ausgewertet:
 * Wer die Systemeinstellung danach ändert, bekommt bis zum nächsten
 * Neuladen weiter die alte Antwort — und in einer installierten PWA, die
 * tagelang offen bleibt, ist „bis zum nächsten Neuladen" lang.
 */
export const pressMorph = () =>
  ({
    whileTap: prefersReducedMotion()
      ? {}
      : { scale: 0.97, borderRadius: 'var(--md-sys-shape-pressed)' },
    transition: springSnappy(),
  }) as const
